import {
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';

import {
  AuthService,
  callable,
  Collection,
  FirestoreService,
} from 'modules/firebase';
import { useRecoilState, useRecoilValue } from 'recoil';
import { UpdateProfileData, UserProfileUpdateConstructor } from '../models';
import { AuthAtoms } from '../recoil';
import { uploadFileToCloudflare } from 'modules/cloudflare';

const Auth = new AuthService();
const UserInfoCollection = new FirestoreService<CustomUserInfo>(
  Collection.UserInfo,
);

export function useAuthentication() {
  const user = useRecoilValue(AuthAtoms.user);
  const isLoggedIn = useRecoilValue(AuthAtoms.isLoggedIn);
  const [loading, setLoading] = useRecoilState(AuthAtoms.loading);

  const loginAsync = async (
    email: string,
    password: string,
    rememberMe: boolean,
  ) => {
    try {
      setLoading(true);
      return await Auth.loginWithEmailAndPasswordAsync(
        email,
        password,
        rememberMe,
      );
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const registerAsync = async (data: RegistrationData) => {
    try {
      setLoading(true);

      const callableFn = callable<
        RegistrationData,
        { success: true; payload: string }
      >('registerUser');

      await callableFn(data);
      await Auth.loginWithEmailAndPasswordAsync(data.email, data.password, true);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const sendResetEmail = async (email: string) => {
    await Auth.sendPasswordResetEmail(email);
  };

  const updateUserEmail = async (oldEmail: string, newEmail: string) => {
    const callableFn = callable<
      { oldEmail: string; newEmail: string },
      { success: true; payload: string }
    >('updateUserEmail');

    // The cloud function throws HttpsError on every failure path, so the
    // callable promise rejects and this await propagates the error.
    const response = await callableFn({ oldEmail, newEmail });
    return response.data;
  };

  const updateProfile = async (id: string, data: UpdateProfileData) => {
    const { avatar, email, oldEmail } = data;
    const shouldUpdateEmail = email !== oldEmail;

    if (avatar) {
      const { url } = await uploadFileToCloudflare('avatars', avatar);
      await UserInfoCollection.set(id, { photoURL: url });
    }

    // Change the email first: the cloud function updates Auth and the userInfo
    // doc together, so we never persist a new email locally that Auth rejected.
    if (shouldUpdateEmail) {
      setLoading(true);
      try {
        await updateUserEmail(oldEmail, email);
      } finally {
        setLoading(false);
      }
    }

    const updatedProfile = new UserProfileUpdateConstructor(data);
    await UserInfoCollection.set(id, updatedProfile);

    // Only the account whose credential actually changed must re-authenticate.
    // An admin editing another user stays logged in; that user's sessions are
    // revoked server-side instead.
    if (shouldUpdateEmail && user?.id === id) {
      Auth.logoutAsync();
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    const user = Auth.auth.currentUser!;
    if (!user || !user.email) {
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );

      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      Auth.logoutAsync();
    } catch (error) {
      throw error;
    }
  };

  const checkEmailExist = async (email: string) => {
    email = email.trim();

    let result = await fetchSignInMethodsForEmail(Auth.auth, email);

    // Check for lowercased user email just in case.
    if (!result || !result.length) {
      result = await fetchSignInMethodsForEmail(Auth.auth, email.toLowerCase());
    }

    return Boolean(result?.length);
  };

  return {
    user,
    loading,
    isLoggedIn,
    loginAsync,
    logoutAsync: Auth.logoutAsync,
    registerAsync,
    changePassword,
    updateProfile,
    updateUserEmail,
    sendResetEmail,
    checkEmailExist,
  };
}
